import {
  CodeRangeError,
  InvalidCodeRangeError,
  RangeTooLargeError,
} from "./codeRangeErrors";

/**
 * Every dash-like character a range might arrive with. Word, Google Docs and macOS all silently turn
 * "80000-89999" into "80000–89999" (an EN DASH, U+2013), and contract PDFs use em dashes and the
 * Unicode minus. CLE-2750: a lab range pasted with an en dash was not recognised as a range, so it
 * "passed through as the literal string" and was saved as one unmatched code — the rule never fired.
 */
const DASH_VARIANTS = /[‐-―−]/g;

// Re-exported so existing `import { RangeTooLargeError } from "helpers/codeRanges"` keeps working.
export { CodeRangeError, InvalidCodeRangeError, RangeTooLargeError };

/**
 * Expand a possibly-range code string into the flat list of codes it represents.
 *
 * Recognized shapes:
 *   - Single literal       "96523"            → ["96523"]
 *   - Numeric range        "0272-0372"        → ["0272", …, "0372"]   (padded to endpoint width)
 *   - Single-letter HCPCS  "J1642-J1700"      → ["J1642", …, "J1700"]
 *
 * A token that CONTAINS a dash but does not parse as a range throws {@link InvalidCodeRangeError}.
 * It used to pass through as the literal string on the theory that "an unrecognized token simply
 * won't match" — which is exactly the failure mode: the rule saves fine, looks fine in the editor,
 * and silently never fires (CLE-2750). The backend rejects such tokens too; this is the friendly
 * message in front of that.
 */
export function expandRange(input: string): string[] {
  const trimmed = input.trim().replace(DASH_VARIANTS, "-");
  if (!trimmed) return [];
  if (!trimmed.includes("-")) return [trimmed];

  const parts = trimmed.split("-");
  if (parts.length !== 2) throw new InvalidCodeRangeError(trimmed);

  const lo = parts[0].trim();
  const hi = parts[1].trim();
  if (!lo || !hi) throw new InvalidCodeRangeError(trimmed);
  // Both endpoints must be the same width — "1-100" is ambiguous.
  if (lo.length !== hi.length) throw new InvalidCodeRangeError(trimmed);
  if (lo > hi) throw new InvalidCodeRangeError(trimmed);

  // Numeric range (DRGs like "001-999", numeric CPTs like "0272-0372").
  if (/^\d+$/.test(lo) && /^\d+$/.test(hi)) {
    const padLen = lo.length;
    const start = parseInt(lo, 10);
    const end = parseInt(hi, 10);
    const result: string[] = [];
    for (let n = start; n <= end; n += 1) {
      result.push(String(n).padStart(padLen, "0"));
    }
    return result;
  }

  // Alphanumeric range (HCPCS Level II — "J1642-J1700", "A0021-A0030").
  const loMatch = lo.match(/^([A-Z])(\d+)$/);
  const hiMatch = hi.match(/^([A-Z])(\d+)$/);
  if (loMatch && hiMatch && loMatch[1] === hiMatch[1]) {
    const prefix = loMatch[1];
    const padLen = loMatch[2].length;
    const start = parseInt(loMatch[2], 10);
    const end = parseInt(hiMatch[2], 10);
    const result: string[] = [];
    for (let n = start; n <= end; n += 1) {
      result.push(`${prefix}${String(n).padStart(padLen, "0")}`);
    }
    return result;
  }

  throw new InvalidCodeRangeError(trimmed);
}

interface ICodeShape {
  kind: "num" | "alpha";
  prefix: string; // "" for a pure-numeric code, the single letter for HCPCS
  width: number; // digit count — two codes only share a run if widths match
  value: number; // numeric value of the digit portion
}

/**
 * Classify a single code for range collapsing. Returns null for anything that
 * isn't a pure-numeric or single-letter-prefixed code (those never collapse).
 */
function codeShape(code: string): ICodeShape | null {
  if (/^\d+$/.test(code)) {
    return { kind: "num", prefix: "", width: code.length, value: Number(code) };
  }
  const match = code.match(/^([A-Z])(\d+)$/);
  if (match) {
    return {
      kind: "alpha",
      prefix: match[1],
      width: match[2].length,
      value: Number(match[2]),
    };
  }
  return null;
}

/** Runs shorter than this stay as individual codes — a 2-code "range" isn't worth it. */
export const RANGE_COLLAPSE_MIN_RUN = 3;

/**
 * Inverse of {@link expandRange}: fold runs of consecutive same-shape codes back
 * into `lo-hi` range strings so a stored flat list reads as the ranges a user
 * would have typed.
 *
 *   ["0272","0273","0274","96523"] → ["0272-0274", "96523"]
 *
 * Walks the list in order — only already-adjacent ascending runs collapse, so the
 * result round-trips cleanly through `expandRange`. Unparseable codes and short
 * runs (< RANGE_COLLAPSE_MIN_RUN) pass through untouched.
 */
export function collapseToRanges(codes: string[]): string[] {
  const result: string[] = [];
  let i = 0;
  while (i < codes.length) {
    const startShape = codeShape(codes[i]);
    if (!startShape) {
      result.push(codes[i]);
      i += 1;
    } else {
      // Extend the run while each next code is the immediate successor.
      let end = i;
      let lastValue = startShape.value;
      while (end + 1 < codes.length) {
        const nextShape = codeShape(codes[end + 1]);
        if (
          !nextShape ||
          nextShape.kind !== startShape.kind ||
          nextShape.prefix !== startShape.prefix ||
          nextShape.width !== startShape.width ||
          nextShape.value !== lastValue + 1
        ) {
          break;
        }
        lastValue = nextShape.value;
        end += 1;
      }
      if (end - i + 1 >= RANGE_COLLAPSE_MIN_RUN) {
        result.push(`${codes[i]}-${codes[end]}`);
        i = end + 1;
      } else {
        result.push(codes[i]);
        i += 1;
      }
    }
  }
  return result;
}

/**
 * Any individual range that expands to more than this is rejected.
 *
 * Was 1000, which rejected the single most common contract range — the whole CPT lab section,
 * 80000-89999, is exactly 10,000 codes — with "Range too large — please narrow it" (CLE-2750).
 * 10,000 admits every full CPT section (each is a 10,000-code block). The stored arrays are a few
 * tens of KB and Postgres `&&` / JS `.includes` are indifferent at this size; the cap only exists to
 * catch a typo like "0-99999" before it becomes a 100,000-element row.
 */
export const RANGE_EXPANSION_LIMIT = 10000;
/** Per spec: post-expansion no code may be longer than this. */
export const CODE_MAX_LENGTH = 32;

/**
 * Normalize the chip list before sending to the backend.
 * - splits any embedded whitespace / comma so a pasted column lands as separate tokens
 * - expands ranges to flat lists (throws RangeTooLargeError if any single range > limit)
 * - drops codes that exceed the sanity length cap
 * - dedupes (preserves first-seen order)
 */
export function prepareCodesForSubmit(chips: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  chips.forEach((raw) => {
    String(raw)
      .split(/[\s,]+/)
      .forEach((token) => {
        if (!token) return;
        const expanded = expandRange(token);
        if (expanded.length > RANGE_EXPANSION_LIMIT) {
          throw new RangeTooLargeError(token, expanded.length);
        }
        expanded.forEach((code) => {
          if (!code) return;
          if (code.length > CODE_MAX_LENGTH) return;
          if (seen.has(code)) return;
          seen.add(code);
          result.push(code);
        });
      });
  });

  return result;
}

/**
 * Expand a chip list to its flat set of codes without throwing — for client-side
 * comparisons (e.g. overlap detection) where a range chip must be compared against
 * individual codes. Unlike {@link prepareCodesForSubmit} this never rejects large
 * ranges, since it produces no persisted data.
 */
export function expandCodeList(chips: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  chips.forEach((raw) => {
    String(raw)
      .split(/[\s,]+/)
      .forEach((token) => {
        if (!token) return;
        // Keep the "never throws" contract: this runs on in-progress input (a half-typed "0272-"
        // is normal mid-edit), so a malformed token is compared as its literal text rather than
        // aborting the comparison. Only submit (prepareCodesForSubmit) is strict.
        let expanded: string[];
        try {
          expanded = expandRange(token);
        } catch (err) {
          if (!(err instanceof InvalidCodeRangeError)) throw err;
          expanded = [token];
        }
        expanded.forEach((code) => {
          if (code && !seen.has(code)) {
            seen.add(code);
            result.push(code);
          }
        });
      });
  });
  return result;
}

/** Intersection of two code lists, in the order they appear in the first list. */
export function intersectCodes(a: string[], b: string[]): string[] {
  if (a.length === 0 || b.length === 0) return [];
  const bSet = new Set(b);
  return a.filter((code) => bSet.has(code));
}
